// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip, Typography } from '@mui/material';
import { IconButton } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import type { IlliquidLinkableProduct } from '../../utils/illiquid-linked-products';
import { IlliquidAssetAccountProductSelector } from './illiquid-asset-account-product-selector';
import { IlliquidLinkedProductContent } from './illiquid-linked-product-content';
import styles from './illiquid-asset-linked-accounts-section.module.css';
import {
  useIlliquidAssetLinkedAccountsSection,
  type UseIlliquidAssetLinkedAccountsSectionProps,
} from './use-illiquid-asset-linked-accounts-section';

export type { UseIlliquidAssetLinkedAccountsSectionProps };

const LinkedProductRow = ({
  product,
  onUnlink,
  t,
}: {
  readonly product: IlliquidLinkableProduct;
  readonly onUnlink: (iban: string) => void;
  readonly t: ReturnType<typeof useIlliquidAssetLinkedAccountsSection>['t'];
}) => {
  const unlinkLabel = t('common.unlink-account', { ns: 'common' });

  return (
    <li className={styles['linked-row']}>
      <IlliquidLinkedProductContent
        product={product}
        t={t}
        surface="section"
        className={styles['option-row-flush']}
      />

      <Tooltip title={unlinkLabel}>
        <span>
          <IconButton
            className={styles['unlink-button']}
            icon="Close"
            variant="icon"
            color="main"
            size="base"
            aria-label={unlinkLabel}
            onClick={() => onUnlink(product.iban)}
          />
        </span>
      </Tooltip>
    </li>
  );
};

export const IlliquidAssetLinkedAccountsSection = defineComponent({
  displayName: 'IlliquidAssetLinkedAccountsSection',
  useSetup: useIlliquidAssetLinkedAccountsSection,
  View: ({ t, linkedProducts, availableProducts, onLink, onUnlink }) => (
    <div className={styles['linked-accounts-section']}>
      <Typography className={styles['section-title']} component="h3">
        <Translation
          i18nKey="illiquid-asset-drawer.section.linked-accounts"
          ns="components"
        />
      </Typography>

      <IlliquidAssetAccountProductSelector
        availableProducts={availableProducts}
        onLink={onLink}
      />

      {linkedProducts.length > 0 && (
        <ul className={styles['linked-list']} data-scrollable>
          {linkedProducts.map(product => (
            <LinkedProductRow
              key={product.iban}
              product={product}
              onUnlink={onUnlink}
              t={t}
            />
          ))}
        </ul>
      )}
    </div>
  ),
});
